/**
 * Duration formatting that picks its own unit.
 *
 * A fixed unit is wrong at both ends: "1030ms" makes the reader do the division,
 * and "0.05s" throws away the precision that mattered. So the unit follows the
 * magnitude — milliseconds below a second, seconds below a minute, minutes above
 * that — and the number keeps roughly three significant figures either way.
 *
 * Null is "—", not 0. A missing latency is unknown, which is a different fact
 * from a latency of zero.
 */

/** One duration, in whichever unit reads best at that magnitude. */
export function formatDuration(ms) {
  if (ms == null) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 0) return '—';

  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 10000) return `${(n / 1000).toFixed(2)}s`;
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`;

  const totalSeconds = Math.round(n / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
}

/**
 * A single unit for a whole axis, chosen from the largest value on it.
 *
 * Per-tick units would let one axis read "800ms, 1.2s, 2.4s" — three scales on
 * one line, which is unreadable and makes the gridlines lie about spacing. The
 * axis commits to one unit and the ticks are bare numbers in it.
 *
 * Returns { unit, toUnit, tick } — `toUnit` converts ms into the axis unit and
 * `tick` renders a tick label without the suffix (recharts appends `unit`).
 */
export function durationAxis(maxMs) {
  const max = Number(maxMs) || 0;

  if (max < 1000) {
    return { unit: 'ms', toUnit: (v) => v, tick: (v) => String(Math.round(v)) };
  }
  if (max < 60000) {
    return {
      unit: 's',
      toUnit: (v) => v / 1000,
      // Sub-10s axes need a decimal or every tick collapses onto "1", "2".
      tick: (v) => (max < 10000 ? Number(v).toFixed(1) : String(Math.round(v))),
    };
  }
  return {
    unit: 'm',
    toUnit: (v) => v / 60000,
    tick: (v) => (max < 600000 ? Number(v).toFixed(1) : String(Math.round(v))),
  };
}
